import { useId } from 'react'
import { useProducts } from '../context/ProductsContext'

/**
 * Photorealistic SVG pharmaceutical vial — compact proportions.
 * White paper label with dark ink, borosilicate glass, aluminum crimp cap.
 */
export default function PeptideVial({
  fullPage = false,
  labelImageSrc,      // kept for API compat
  labelAlt = '',
  formulaName = 'BPC-157',
  formulaSubtitle = 'Body protection compound',
  concentration = '5 mg',
  labelSize,          // short label for dosage box (e.g. "12 MG") — falls back to concentration
  wrapClassName = 'peptide-vial-float',
  rootClassName = '',
}) {
  const uid = useId().replace(/:/g, '')
  const g = (n) => `${n}-${uid}`
  const { branding } = useProducts()
  const logoSrc = branding?.logoDark?.url ?? '/logos/lookslab-logo.png'

  // ── Geometry (viewBox 120 × 208) ──────────────────────────
  const W = 120, H = 208

  // Cap (aluminum crimp)
  const CAP_X = 26, CAP_W = 68, CAP_Y = 2,  CAP_H = 20
  // Flange / skirt
  const FLG_X = 24, FLG_W = 72, FLG_Y = 21, FLG_H = 4
  // Rubber stopper
  const RUB_X = 33, RUB_W = 54, RUB_Y = 25, RUB_H = 7
  // Neck
  const NCK_X = 33, NCK_W = 54, NCK_Y = 32, NCK_H = 10
  // Shoulder
  const SH_Y = 42, SH_H = 12
  // Body
  const BDY_X = 10, BDY_W = 100, BDY_Y = 54, BDY_H = 118
  // Base
  const BSE_X = 10, BSE_W = 100, BSE_Y = 172, BSE_H = 10

  const MID = W / 2

  // Glass gradient spans body width in userSpace coords
  const GX1 = BDY_X, GX2 = BDY_X + BDY_W

  const shPath = [
    `M ${NCK_X} ${SH_Y}`,
    `L ${NCK_X + NCK_W} ${SH_Y}`,
    `L ${BDY_X + BDY_W} ${SH_Y + SH_H}`,
    `L ${BDY_X} ${SH_Y + SH_H}`,
    'Z',
  ].join(' ')

  const bsPath = [
    `M ${BSE_X} ${BSE_Y}`,
    `L ${BSE_X + BSE_W} ${BSE_Y}`,
    `L ${BSE_X + BSE_W} ${BSE_Y + BSE_H - 6}`,
    `Q ${BSE_X + BSE_W} ${BSE_Y + BSE_H} ${BSE_X + BSE_W - 7} ${BSE_Y + BSE_H}`,
    `L ${BSE_X + 7} ${BSE_Y + BSE_H}`,
    `Q ${BSE_X} ${BSE_Y + BSE_H} ${BSE_X} ${BSE_Y + BSE_H - 6}`,
    'Z',
  ].join(' ')

  // Label geometry
  const LBL_X = BDY_X + 4
  const LBL_W = BDY_W - 8
  const LBL_Y = BDY_Y + 6
  const LBL_H = BDY_H - 28      // tighter: more label, less glass showing
  const LMX = LBL_X + LBL_W / 2

  // Scale compound name first — band height derives from font size
  const nameDisplay = formulaName.toUpperCase()
  const nameFs = nameDisplay.length <= 5 ? 16
    : nameDisplay.length <= 8  ? 13
    : nameDisplay.length <= 11 ? 10
    : 8
  const useTextLength = nameDisplay.length > 9

  // Three zones
  const Z1_H_LOGO = 20                     // logo itself
  const Z2_H = nameFs + 10                 // name band — snug to text
  const Z3_BADGE_H_CONST = 11              // fixed badge height

  // Free vertical space between logo and badge
  const CONTENT_H  = Z2_H + 13 + 8 + 6    // band + box + gap + lyo ≈ band + 27
  const FREE_SPACE  = LBL_H - Z1_H_LOGO - CONTENT_H - Z3_BADGE_H_CONST
  const PAD         = Math.max(2, Math.round(FREE_SPACE / 2))  // split evenly

  // PAD before name band (extends Z1 zone), PAD before dosage box (Z3 top)
  const Z1_H = Z1_H_LOGO + PAD            // logo + gap = extended top zone
  const Z3_H = LBL_H - Z1_H - Z2_H

  const Z1_Y = LBL_Y
  const Z2_Y = LBL_Y + Z1_H
  const Z3_Y = LBL_Y + Z1_H + Z2_H

  // Name baseline: 5px top pad + cap-height
  const Z2_TEXT_Y = Z2_Y + 5 + Math.round(nameFs * 0.74)

  // Zone 3 layout — dosage box starts PAD from top (mirrors top gap)
  const Z3_BOX_TOP = PAD                   // same gap as above name band
  const Z3_BOX_H   = 13                    // dosage box height
  const Z3_LYO_Y   = Z3_BOX_TOP + Z3_BOX_H + 7  // lyo baseline
  const Z3_BADGE_H   = Z3_BADGE_H_CONST    // 11px — tight badge
  const Z3_BADGE_TOP = Z3_H - Z3_BADGE_H  // pinned to label bottom

  const svg = (
    <svg
      className={wrapClassName}
      viewBox={`0 0 ${W} ${H}`}
      width={W}
      height={H}
      style={{ display: 'block', maxWidth: '100%', overflow: 'visible' }}
      aria-label={`${formulaName} ${concentration}`}
      role="img"
    >
      <defs>
        {/* Metallic cap */}
        <linearGradient id={g('cap')} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%"   stopColor="#e2e2e2" />
          <stop offset="5%"   stopColor="#f8f8f8" />
          <stop offset="20%"  stopColor="#bdbdbd" />
          <stop offset="36%"  stopColor="#d4d4d4" />
          <stop offset="52%"  stopColor="#979797" />
          <stop offset="68%"  stopColor="#b3b3b3" />
          <stop offset="84%"  stopColor="#6e6e6e" />
          <stop offset="100%" stopColor="#888888" />
        </linearGradient>
        <linearGradient id={g('capx')} x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%"   stopColor="rgba(0,0,0,0.14)" />
          <stop offset="22%"  stopColor="rgba(255,255,255,0.20)" />
          <stop offset="50%"  stopColor="rgba(255,255,255,0.05)" />
          <stop offset="78%"  stopColor="rgba(255,255,255,0.13)" />
          <stop offset="100%" stopColor="rgba(0,0,0,0.18)" />
        </linearGradient>
        <pattern id={g('knurl')} x="0" y="0" width="2.4" height="1" patternUnits="userSpaceOnUse">
          <rect x="0" y="0" width="1.0" height={CAP_H} fill="rgba(0,0,0,0.055)" />
        </pattern>

        {/* Cap port */}
        <radialGradient id={g('port')} cx="36%" cy="30%" r="66%">
          <stop offset="0%"   stopColor="#c8c8c8" />
          <stop offset="55%"  stopColor="#848484" />
          <stop offset="100%" stopColor="#505050" />
        </radialGradient>
        <radialGradient id={g('hole')} cx="38%" cy="35%" r="65%">
          <stop offset="0%"   stopColor="#404040" />
          <stop offset="100%" stopColor="#101010" />
        </radialGradient>

        {/* Flange */}
        <linearGradient id={g('flange')} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%"   stopColor="#8c8c8c" />
          <stop offset="45%"  stopColor="#545454" />
          <stop offset="100%" stopColor="#303030" />
        </linearGradient>

        {/* Rubber stopper */}
        <linearGradient id={g('rubber')} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%"   stopColor="#646464" />
          <stop offset="40%"  stopColor="#444444" />
          <stop offset="100%" stopColor="#2a2a2a" />
        </linearGradient>

        {/* Borosilicate glass (horizontal, userSpace) */}
        <linearGradient id={g('glass')} x1={GX1} y1="0" x2={GX2} y2="0"
          gradientUnits="userSpaceOnUse">
          <stop offset="0%"    stopColor="rgba(14,18,26,0.90)" />
          <stop offset="3%"    stopColor="rgba(44,52,68,0.50)" />
          <stop offset="9%"    stopColor="rgba(130,145,168,0.22)" />
          <stop offset="14%"   stopColor="rgba(255,255,255,0.46)" />
          <stop offset="21%"   stopColor="rgba(248,252,255,0.12)" />
          <stop offset="38%"   stopColor="rgba(218,228,242,0.04)" />
          <stop offset="50%"   stopColor="rgba(210,220,236,0.03)" />
          <stop offset="62%"   stopColor="rgba(218,228,242,0.04)" />
          <stop offset="79%"   stopColor="rgba(248,252,255,0.09)" />
          <stop offset="88%"   stopColor="rgba(145,158,180,0.20)" />
          <stop offset="95%"   stopColor="rgba(36,42,56,0.54)" />
          <stop offset="100%"  stopColor="rgba(10,14,22,0.86)" />
        </linearGradient>
        <radialGradient id={g('glow')} cx="50%" cy="44%" r="46%">
          <stop offset="0%"   stopColor="rgba(255,255,255,0.05)" />
          <stop offset="100%" stopColor="rgba(0,0,0,0)" />
        </radialGradient>

        {/* Lyophilized cake */}
        <linearGradient id={g('cake')} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%"   stopColor="rgba(255,255,255,0)" />
          <stop offset="25%"  stopColor="rgba(248,244,232,0.14)" />
          <stop offset="65%"  stopColor="rgba(236,228,210,0.52)" />
          <stop offset="100%" stopColor="rgba(214,205,183,0.76)" />
        </linearGradient>

        {/* ── Label 3D wrap gradients ── */}

        {/* Cylinder shadow: strong darkening at left/right edges */}
        <linearGradient id={g('cylshadow')} x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%"   stopColor="rgba(0,0,0,0.84)" />
          <stop offset="7%"   stopColor="rgba(0,0,0,0.58)" />
          <stop offset="15%"  stopColor="rgba(0,0,0,0.24)" />
          <stop offset="24%"  stopColor="rgba(0,0,0,0.05)" />
          <stop offset="32%"  stopColor="rgba(0,0,0,0)" />
          <stop offset="68%"  stopColor="rgba(0,0,0,0)" />
          <stop offset="78%"  stopColor="rgba(0,0,0,0.06)" />
          <stop offset="87%"  stopColor="rgba(0,0,0,0.26)" />
          <stop offset="94%"  stopColor="rgba(0,0,0,0.56)" />
          <stop offset="100%" stopColor="rgba(0,0,0,0.82)" />
        </linearGradient>

        {/* Specular highlight: bright streak just left of center (single light source) */}
        <linearGradient id={g('cylspec')} x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%"   stopColor="rgba(255,255,255,0)" />
          <stop offset="14%"  stopColor="rgba(255,255,255,0.05)" />
          <stop offset="20%"  stopColor="rgba(255,255,255,0.22)" />
          <stop offset="26%"  stopColor="rgba(255,255,255,0.07)" />
          <stop offset="36%"  stopColor="rgba(255,255,255,0)" />
          <stop offset="100%" stopColor="rgba(255,255,255,0)" />
        </linearGradient>

        {/* Paper radial: center slightly brighter → label surface bowing toward viewer */}
        <radialGradient id={g('lblrad')} cx="42%" cy="50%" r="55%">
          <stop offset="0%"   stopColor="rgba(255,255,255,0.18)" />
          <stop offset="100%" stopColor="rgba(0,0,0,0)" />
        </radialGradient>

        {/* Top edge shadow: paper pressed against curve at top */}
        <linearGradient id={g('topedge')} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%"   stopColor="rgba(0,0,0,0.22)" />
          <stop offset="100%" stopColor="rgba(0,0,0,0)" />
        </linearGradient>

        {/* Bottom edge shadow */}
        <linearGradient id={g('botedge')} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%"   stopColor="rgba(0,0,0,0)" />
          <stop offset="100%" stopColor="rgba(0,0,0,0.20)" />
        </linearGradient>

        {/* Label rounded-corner clip */}
        <clipPath id={g('lblclip')}>
          <rect x={LBL_X} y={LBL_Y} width={LBL_W} height={LBL_H} rx="2" />
        </clipPath>

        {/* Ground shadow */}
        <radialGradient id={g('shadow')} cx="50%" cy="12%" rx="50%" ry="100%">
          <stop offset="0%"   stopColor="rgba(0,0,0,0.52)" />
          <stop offset="50%"  stopColor="rgba(0,0,0,0.17)" />
          <stop offset="100%" stopColor="rgba(0,0,0,0)" />
        </radialGradient>

        {/* Clip body content */}
        <clipPath id={g('bclip')}>
          <rect x={BDY_X} y={BDY_Y} width={BDY_W} height={BDY_H} />
        </clipPath>
      </defs>

      {/* Ground shadow */}
      <ellipse cx={MID} cy={BSE_Y + BSE_H + 10} rx={42} ry={8}
        fill={`url(#${g('shadow')})`} />

      {/* BASE */}
      <path d={bsPath}
        fill={`url(#${g('glass')})`}
        stroke="rgba(10,13,21,0.60)"
        strokeWidth="0.6"
      />
      <path d={bsPath} fill={`url(#${g('glow')})`} stroke="none" />

      {/* BODY */}
      <rect x={BDY_X} y={BDY_Y} width={BDY_W} height={BDY_H}
        fill={`url(#${g('glass')})`} />
      <rect x={BDY_X} y={BDY_Y} width={BDY_W} height={BDY_H}
        fill={`url(#${g('glow')})`} />
      <line x1={BDY_X + 0.5} y1={BDY_Y} x2={BDY_X + 0.5} y2={BDY_Y + BDY_H}
        stroke="rgba(162,180,205,0.30)" strokeWidth="0.7" />
      <line x1={BDY_X + BDY_W - 0.5} y1={BDY_Y} x2={BDY_X + BDY_W - 0.5} y2={BDY_Y + BDY_H}
        stroke="rgba(10,14,22,0.55)" strokeWidth="0.7" />

      {/* LYOPHILIZED CAKE (clipped) */}
      <g clipPath={`url(#${g('bclip')})`}>
        <rect x={BDY_X} y={BDY_Y + BDY_H - 32} width={BDY_W} height={32}
          fill={`url(#${g('cake')})`} />
        <ellipse cx={MID} cy={BDY_Y + BDY_H - 4} rx={38} ry={14}
          fill="rgba(226,216,193,0.60)" />
        <ellipse cx={MID - 9} cy={BDY_Y + BDY_H - 4} rx={20} ry={9}
          fill="rgba(215,205,181,0.44)" />
        <ellipse cx={MID + 5} cy={BDY_Y + BDY_H - 17} rx={13} ry={4}
          fill="rgba(255,252,244,0.11)" />
      </g>

      {/* ── THREE-ZONE LABEL — outer body clip, inner label clip for 3D wrap ── */}
      <g clipPath={`url(#${g('bclip')})`}>
        {/* Everything inside the label's own rounded-corner clip */}
        <g clipPath={`url(#${g('lblclip')})`}>

          {/* ── ZONE 1: white top — logo lockup ── */}
          <rect x={LBL_X} y={Z1_Y} width={LBL_W} height={Z1_H} fill="#f5f4f1" />
          <rect x={LBL_X} y={Z1_Y} width={LBL_W} height={Z1_H}
            fill={`url(#${g('lblrad')})`} />

          {/* Logo — transparent PNG, no blend mode needed */}
          <image
            href={logoSrc}
            x={LBL_X + 3}
            y={Z1_Y + 1}
            width={LBL_W - 6}
            height={Z1_H - 2}
            preserveAspectRatio="xMidYMid meet"
          />

          {/* ── ZONE 2: black band — compound name ── */}
          <rect x={LBL_X} y={Z2_Y} width={LBL_W} height={Z2_H} fill="#111111" />

          <text
            x={LMX}
            y={Z2_TEXT_Y}
            textAnchor="middle"
            fontFamily="'Inter', 'Helvetica Neue', sans-serif"
            fontSize={nameFs}
            fontWeight="900"
            letterSpacing="0.01em"
            fill="#ffffff"
            {...(useTextLength ? { textLength: LBL_W - 8, lengthAdjust: 'spacingAndGlyphs' } : {})}
          >{nameDisplay}</text>

          {/* ── ZONE 3: white bottom — dosage & badge ── */}
          <rect x={LBL_X} y={Z3_Y} width={LBL_W} height={Z3_H} fill="#f5f4f1" />
          <rect x={LBL_X} y={Z3_Y} width={LBL_W} height={Z3_H}
            fill={`url(#${g('lblrad')})`} />

          {/* Dosage box */}
          <rect
            x={LBL_X + 7} y={Z3_Y + Z3_BOX_TOP}
            width={44} height={Z3_BOX_H}
            fill="none" stroke="#1a1a1a" strokeWidth="1.0" rx="0.5"
          />
          <text
            x={LBL_X + 29} y={Z3_Y + Z3_BOX_TOP + Math.round(Z3_BOX_H * 0.72)}
            textAnchor="middle"
            fontFamily="'Inter', 'Helvetica Neue', sans-serif"
            fontSize="9" fontWeight="800" letterSpacing="0.01em"
            fill="#111111"
          >{(labelSize ?? concentration).toUpperCase()}</text>

          {/* LYOPHILIZED POWDER — aligned to box width */}
          <text
            x={LBL_X + 7} y={Z3_Y + Z3_LYO_Y}
            textAnchor="start"
            fontFamily="'Inter', 'Helvetica Neue', sans-serif"
            fontSize="5" fontWeight="600"
            fill="#555555"
            textLength={44}
            lengthAdjust="spacingAndGlyphs"
          >LYOPHILIZED POWDER</text>

          {/* FOR LABORATORY USE ONLY — tight badge pinned to label bottom */}
          <rect
            x={LBL_X} y={Z3_Y + Z3_BADGE_TOP}
            width={LBL_W} height={Z3_BADGE_H}
            fill="#111111"
          />
          <text
            x={LMX}
            y={Z3_Y + Z3_BADGE_TOP + 8}
            textAnchor="middle"
            fontFamily="'Inter', 'Helvetica Neue', sans-serif"
            fontSize="5.5" fontWeight="700" letterSpacing="0.08em"
            fill="#efefef"
          >FOR LABORATORY USE ONLY</text>

          {/* ── 3D CYLINDER WRAP LAYERS (paint on top of all content) ── */}

          {/* 1. Primary cylinder shadow — strong edge darkening */}
          <rect x={LBL_X} y={LBL_Y} width={LBL_W} height={LBL_H}
            fill={`url(#${g('cylshadow')})`} />

          {/* 2. Specular highlight — bright streak ~20% from left */}
          <rect x={LBL_X} y={LBL_Y} width={LBL_W} height={LBL_H}
            fill={`url(#${g('cylspec')})`} />

          {/* 3. Top edge shadow — paper pressed against cylinder curve */}
          <rect x={LBL_X} y={LBL_Y} width={LBL_W} height={8}
            fill={`url(#${g('topedge')})`} />

          {/* 4. Bottom edge shadow */}
          <rect x={LBL_X} y={LBL_Y + LBL_H - 8} width={LBL_W} height={8}
            fill={`url(#${g('botedge')})`} />

        </g>{/* end lblclip */}
      </g>

      {/* SHOULDER */}
      <path d={shPath} fill={`url(#${g('glass')})`} stroke="none" />
      <path d={shPath} fill={`url(#${g('glow')})`} stroke="none" />
      <line x1={NCK_X} y1={SH_Y} x2={NCK_X + NCK_W} y2={SH_Y}
        stroke="rgba(155,172,196,0.20)" strokeWidth="0.5" />
      <line x1={NCK_X} y1={SH_Y} x2={BDY_X} y2={SH_Y + SH_H}
        stroke="rgba(158,175,200,0.24)" strokeWidth="0.6" />
      <line x1={NCK_X + NCK_W} y1={SH_Y} x2={BDY_X + BDY_W} y2={SH_Y + SH_H}
        stroke="rgba(10,14,22,0.48)" strokeWidth="0.6" />

      {/* NECK */}
      <rect x={NCK_X} y={NCK_Y} width={NCK_W} height={NCK_H}
        fill={`url(#${g('glass')})`} />
      <line x1={NCK_X + 0.4} y1={NCK_Y} x2={NCK_X + 0.4} y2={NCK_Y + NCK_H}
        stroke="rgba(155,172,196,0.30)" strokeWidth="0.6" />
      <line x1={NCK_X + NCK_W - 0.4} y1={NCK_Y} x2={NCK_X + NCK_W - 0.4} y2={NCK_Y + NCK_H}
        stroke="rgba(10,14,22,0.52)" strokeWidth="0.6" />

      {/* RUBBER STOPPER */}
      <rect x={RUB_X} y={RUB_Y} width={RUB_W} height={RUB_H}
        fill={`url(#${g('rubber')})`} />
      <rect x={RUB_X} y={RUB_Y} width={RUB_W} height={RUB_H * 0.30}
        fill="rgba(255,255,255,0.07)" />
      <line x1={RUB_X} y1={RUB_Y} x2={RUB_X} y2={RUB_Y + RUB_H}
        stroke="rgba(255,255,255,0.06)" strokeWidth="0.5" />
      <line x1={RUB_X + RUB_W} y1={RUB_Y} x2={RUB_X + RUB_W} y2={RUB_Y + RUB_H}
        stroke="rgba(0,0,0,0.32)" strokeWidth="0.5" />

      {/* CAP FLANGE */}
      <path
        d={`M ${FLG_X} ${FLG_Y} L ${FLG_X + FLG_W} ${FLG_Y} L ${FLG_X + FLG_W} ${FLG_Y + FLG_H - 2} Q ${FLG_X + FLG_W} ${FLG_Y + FLG_H} ${FLG_X + FLG_W - 4} ${FLG_Y + FLG_H} L ${FLG_X + 4} ${FLG_Y + FLG_H} Q ${FLG_X} ${FLG_Y + FLG_H} ${FLG_X} ${FLG_Y + FLG_H - 2} Z`}
        fill={`url(#${g('flange')})`}
        stroke="rgba(20,20,20,0.50)"
        strokeWidth="0.5"
      />
      <line x1={FLG_X} y1={FLG_Y + 2} x2={FLG_X + FLG_W} y2={FLG_Y + 2}
        stroke="rgba(0,0,0,0.35)" strokeWidth="1.0" />

      {/* ALUMINUM CAP */}
      <rect x={CAP_X} y={CAP_Y} width={CAP_W} height={CAP_H} rx="3"
        fill={`url(#${g('cap')})`} />
      <rect x={CAP_X} y={CAP_Y} width={CAP_W} height={CAP_H} rx="3"
        fill={`url(#${g('knurl')})`} opacity="0.85" />
      <rect x={CAP_X} y={CAP_Y} width={CAP_W} height={CAP_H} rx="3"
        fill={`url(#${g('capx')})`} />
      <rect x={CAP_X + 4} y={CAP_Y + 1.5} width={CAP_W - 8} height={CAP_H * 0.38} rx="2.5"
        fill="rgba(255,255,255,0.24)" />
      <rect x={CAP_X} y={CAP_Y} width={CAP_W} height={CAP_H} rx="3"
        fill="none" stroke="rgba(40,40,40,0.65)" strokeWidth="0.7" />

    </svg>
  )

  if (fullPage) {
    return (
      <div className={`peptide-vial-page${rootClassName ? ` ${rootClassName}` : ''}`}>
        {svg}
      </div>
    )
  }

  return (
    <div className={rootClassName || undefined}
      style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '100%' }}>
      {svg}
    </div>
  )
}
